import { PageOptions } from '@graphcommerce/framer-next-pages'
import { mergeDeep } from '@graphcommerce/graphql'
import {
  AddProductsToCartButton,
  AddProductsToCartError,
  AddProductsToCartForm,
  AddProductsToCartFormProps,
  AddProductsToCartQuantity,
  getProductStaticPaths,
  jsonLdProduct,
  jsonLdProductOffer,
  ProductCustomizable,
  productPageCategory,
  ProductPageDescription,
  ProductPageMeta,
  ProductPagePriceTiers,
  ProductShortDescription,
} from '@graphcommerce/magento-product'

import { BundleProductOptions } from '@graphcommerce/magento-product-bundle'
import {
  // ConfigurableName,
  ConfigurablePriceTiers,
  ConfigurableProductPageGallery,
  // ConfigurableProductOptions, TODO: Restore next release 6.1.1
  defaultConfigurableOptionsSelection,
} from '@graphcommerce/magento-product-configurable'
import { DownloadableProductOptions } from '@graphcommerce/magento-product-downloadable'
import { jsonLdProductReview, ProductReviewChip } from '@graphcommerce/magento-review'
import { redirectOrNotFound, Money, StoreConfigDocument } from '@graphcommerce/magento-store'
import { ProductWishlistChipDetail } from '@graphcommerce/magento-wishlist'
import {
  GetStaticProps,
  JsonLd,
  LayoutHeader,
  LayoutTitle,
  isTypename,
} from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { i18n } from '@lingui/core'
// import { Box, Divider, Link, Typography } from '@mui/material/Box'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { GetStaticPaths } from 'next'
import {
  LayoutNavigation,
  LayoutNavigationProps,
  RowProduct,
  RowRenderer,
  ProductSidebarDelivery,
  ProductSidebarPreOrder,
  ProductSidebarVideo,
  ProductPagePrice,
  ConfigurablePrice,
  ConfigurableProductOptions /* TODO: Remove in next releas 6.1.1 */
} from '../../components'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { ProductPage2Document, ProductPage2Query } from '../../graphql/ProductPage2.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import {M2Types} from "../../.mesh/sources/m2/types";
import Aggregation = M2Types.Aggregation;
import {ProductPageLoginToBuyButton} from '../../components/LoginToBuyButton'
import { useCustomerSession } from '@graphcommerce/magento-customer/hooks'

function getSizeUrl(products): string|null {
  const { aggregations } = products
  const product = products.items[0]
  const ageMap = {
    "Bebé": "baby",
    "Baby": "baby",
    "Infantil": "kids",
    "Children": "kids",
    "Joven": "youth",
    "Young": "youth",
    "Adulto/a": "adult",
    "Adult": "adult",
    "Todas": "all",
    "All": "all"
  }
  const genderMap = {
    "Hombre": "men",
    "Mujer": "women",
    "Unisex": "unisex",
    "Man": "men",
    "Woman": "women"
  }
  const ageAttr = aggregations.find(aggregation => aggregation.attribute_code === 'age')
  const genderAttr = aggregations.find(aggregation => aggregation.attribute_code === 'gender')
  const manufacturerAttr = aggregations.find(aggregation => aggregation.attribute_code === 'manufacturer')
  const slugify = (str) => (
    str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  )
  const attributeSetSlug = slugify(product.attribute_set_name)
  const age = ageAttr ? ageMap[ageAttr.options[0].label] : 'adult'
  const gender = genderAttr ? genderMap[genderAttr.options[0].label] : 'unisex'
  const manufacturer = manufacturerAttr ? manufacturerAttr.options[0].label.toLowerCase() : 'all'
  return age && gender && manufacturer ? `/modal/product/${attributeSetSlug}/${gender}/${age}/${slugify(manufacturer)}/size` : null
}

function prepareProductAggregations(products): Array<Aggregation> {
  const excludedAggregations = ['category_uid', 'board_volume', 'sail_boom_size']
  const boolAggregations = ['fins_included']
  const m2Aggregations = ['sail_size']
  const extraAggregations = [
    {
      attributeCode: 'board_size',
      attributeLabel: i18n._(/* i18n */ 'Board Size'),
      attributeValue: (value) => value,
      attributePosition: 2
    },
    {
      attributeCode: 'board_volume',
      attributeLabel: i18n._(/* i18n */ 'Volume (l.)'),
      attributeValue: (value) => `${value} l.`,
      attributePosition: 3
    },
    {
      attributeCode: 'fins_included',
      attributeLabel: i18n._(/* i18n */ 'Fins included'),
      attributeValue: (value) => (value === 1 ?  i18n._(/* i18n */ 'Yes') : 'No'),
      attributePosition: 10
    },
    {
      attributeCode: 'sail_boom_size',
      attributeLabel: i18n._(/* i18n */ 'Boom (cm.)'),
      attributeValue: (value) => `${value} cm.`,
      attributePosition: 6
    },
    {
      attributeCode: 'other_features',
      attributeLabel: i18n._(/* i18n */ 'Other features'),
      attributeValue: (value) => value,
      attributePosition: null
    }
  ]
  const { items, aggregations } = products

  const result = aggregations?.
  filter(aggregation => !excludedAggregations.includes(aggregation.attribute_code as string)).
  map((aggregation) => {
      if (boolAggregations.includes(aggregation.attribute_code as string)) {
        return {
          ...aggregation,
          options: [{
            label: aggregation.options[0].label === '0' ? 'No' : i18n._(/* i18n */ 'Yes'),
            value: aggregation.options[0].value === '0' ? 'No' : i18n._(/* i18n */ 'Yes')
          }]
        }
      }
    if (m2Aggregations.includes(aggregation.attribute_code as string)) {
      return {
        ...aggregation,
        options: [{
          label: `${aggregation.options[0].label} m2.`,
          value: aggregation.options[0].value
        }]
      }
    }
      if (aggregation.attribute_code === 'color') {
        return {
          ...aggregation,
          attribute_code: 'specs_color'
        }
      }
      return aggregation
    }
  )

  // Extra aggregation
  if (items.length) {
    extraAggregations.forEach((extra) => {
      if (items[0][extra.attributeCode]) {
        const value = extra.attributeValue(items[0][extra.attributeCode])
        const spec = {
          "attribute_code": extra.attributeCode,
          "count": 1,
          "label": extra.attributeLabel,
          "position": null,
          "options": [
            {
              "count": 1,
              "label": value ?? "",
              "value": ""
            }
          ]
        }
        result.splice(extra.attributePosition ?? result.length, 0, spec)
      }
    })
  }

  return result
}

type Props = ProductPage2Query & Pick<AddProductsToCartFormProps, 'defaultValues'>

type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

function ProductPage(props: Props) {
  const { products, relatedUpsells, pages, defaultValues } = props
  const { loggedIn } = useCustomerSession()
  const product = mergeDeep(products, relatedUpsells)?.items?.[0]
  if (!product?.sku || !product.url_key) return null

  const productDescription = {
    description: product.description,
    name: product.display_name
  }

  if (product.second_hand) {
    productDescription.name = `${productDescription.name} - ${i18n._(/* i18n */ 'Used')}`
  }
  if (product.pre_order) {
    productDescription.name = `${productDescription.name} - ${i18n._(/* i18n */ 'Pre-order')}`
  }

  const sizeUrl = getSizeUrl(products);

  // @ts-ignore
  return (
    <>
      <LayoutHeader floatingMd>
        <LayoutTitle size='small' component='span'>
          {product.name}
        </LayoutTitle>
      </LayoutHeader>
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          ...jsonLdProduct(product),
          ...jsonLdProductOffer(product),
          ...jsonLdProductReview(product),
        }}
      />
      <ProductPageMeta {...product} />

      <AddProductsToCartForm key={product.uid} defaultValues={defaultValues}>
        <ConfigurableProductPageGallery
          url_key={product.url_key}
          media_gallery={product.media_gallery}
          sx={(theme) => ({
            '& .SidebarGallery-sidebar': { display: 'grid', rowGap: theme.spacings.sm },
          })}
        >
          <div>
            {/*isTypename(product, ['ConfigurableProduct', 'BundleProduct']) && (
              <Typography component='div' variant='body2' color='text.disabled'>
                <Trans
                  id='As low as <0/>'
                  components={{ 0: <Money {...product.price_range.minimum_price.final_price} /> }}
                />
              </Typography>
            )*/}

            <Typography variant='h3' component='h1'>
              {product.display_name}{product.second_hand ? ` - ${i18n._(/* i18n */ 'Used')}` : ''}
              {/* isTypename(product, ['ConfigurableProduct']) ? (
                <ConfigurableName product={product} />
              ) : (
                product.display_name
              )*/}
            </Typography>
            <Typography variant='h6' component='div' gutterBottom>
              SKU: {product.sku}
            </Typography>

            <ProductShortDescription
              short_description={product?.short_description}
              sx={(theme) => ({
                mb: theme.spacings.xs,
                mt: theme.spacings.xs
              })}
            />

            <ProductReviewChip rating={product.rating_summary} reviewSectionId='reviews' />
          </div>

          {isTypename(product, ['ConfigurableProduct']) && (
            <ConfigurableProductOptions
              product={product}
              optionEndLabels={{
                size: (sizeUrl &&
                    <Link
                        href={sizeUrl}
                        rel='nofollow'
                        color='primary'
                        underline='hover'
                    >
                        <Trans id='Which size is right?' />
                    </Link>
                ),
                shoe_size: (sizeUrl &&
                    <Link
                        href={sizeUrl}
                        rel='nofollow'
                        color='primary'
                        underline='hover'
                    >
                        <Trans id='Which size is right?' />
                    </Link>
                ),
              }}
            />
          )}
          {isTypename(product, ['BundleProduct']) && (
            <BundleProductOptions product={product} layout='stack' />
          )}
          {isTypename(product, ['DownloadableProduct']) && (
            <DownloadableProductOptions product={product} />
          )}
          {!isTypename(product, ['GroupedProduct']) && <ProductCustomizable product={product} />}

          <Divider />
          {loggedIn &&
              <Box
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: theme.spacings.md,
                  })}
              >
                  <AddProductsToCartQuantity sx={{ flexShrink: '0' }} />

                  <AddProductsToCartError>
                      <Typography component='div' variant='h3' lineHeight='1' sx={{
                        marginLeft: 'auto'
                      }}>
                        {isTypename(product, ['ConfigurableProduct']) ? (
                          <ConfigurablePrice product={product} />
                        ) : (
                          <ProductPagePrice product={product} url_key={product.url_key} />
                        )}
                      </Typography>
                  </AddProductsToCartError>
              </Box>
          }

          {/*
            customer && isTypename(product, ['ConfigurableProduct']) ? (
            <ConfigurablePriceTiers product={product} />
            ) : (
              <ProductPagePriceTiers product={product} />
            )
           */}

          {
            product.pre_order === 1 ?
              <ProductSidebarPreOrder preOrderText={product.pre_order_text} />
            :
              <ProductSidebarDelivery product={product} externalSource={product.external_source} />
          }

            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'start',
                columnGap: theme.spacings.xs,
              })}
            >
              {loggedIn ?
                <>
                  <AddProductsToCartButton fullWidth product={product} />
                  <ProductWishlistChipDetail {...product} />
                </>
                :
                <ProductPageLoginToBuyButton />
              }
          </Box>
        </ConfigurableProductPageGallery>
      </AddProductsToCartForm>

      <ProductPageDescription {...productDescription}
                              right={<ProductSidebarVideo videoUrl={product.video_embed ?? ''} />}
                              fontSize='subtitle1'
                              sx={(theme) => ({
                                [theme.breakpoints.up('md')]: {
                                  gridTemplateAreas: `"top  top" "left right"`,
                                },
                              })}
      />

      {pages?.[0] && (
        <RowRenderer
          content={pages?.[0].content}
          renderer={{
            RowProduct: (rowProps) => (
              <RowProduct
                {...rowProps}
                {...product}
                items={products?.items}
                aggregations={products?.aggregations}
              />
            ),
          }}
        />
      )}
    </>
  )
}

ProductPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default ProductPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { paths: [], fallback: false }
  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const path = (locale: string) => getProductStaticPaths(graphqlSsrClient(locale), locale)
  const paths = (await Promise.all(locales.map(path))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const urlKey = params?.url ?? '??'

  const conf = client.query({ query: StoreConfigDocument })
  const productPage = staticClient.query({
    query: ProductPage2Document,
    variables: { url: 'product/global', urlKey },
  })
  const layout = staticClient.query({ query: LayoutDocument })
  const { data } = (await productPage)
  const product = data.products?.items?.find((p) => p?.url_key === urlKey)

  if (!product) return redirectOrNotFound(staticClient, conf, params, locale)

  const aggregations = prepareProductAggregations(data.products)
  const category = productPageCategory(product)
  const up =
    category?.url_path && category?.name
      ? { href: `/${category.url_path}`, title: category.name }
      : { href: `/`, title: 'Home' }

  const configurableOptionsSelection = defaultConfigurableOptionsSelection(urlKey, client, data)
  if (configurableOptionsSelection.products) {
    configurableOptionsSelection.products = {
      ...configurableOptionsSelection.products,
      aggregations
    }
  }

  return {
    props: {
      ...configurableOptionsSelection,
      ...(await layout).data,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
    },
    revalidate: 60 * 60 * 24,
  }
}
